import React, { ReactNode, useEffect, useRef } from "react";
import { cx } from "~/lib/cx";
import style from "./index.module.scss";

export type VerticalTabKey = string | number;

export interface IVerticalTabItem<T> {
  key: VerticalTabKey;
  id: T;
  title: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  tooltip?: string;
}

interface IVerticalTabsProps<T> {
  items: IVerticalTabItem<T>[];
  activeId: T;
  onChange: (id: T) => void;
  className?: string;
  // When this value changes (and it is non-null), focus moves to the active
  // item, or the first item when none is active. Used to jump focus into the
  // list after a search picks a new context.
  autoFocusKey?: unknown;
}

export const VerticalTabs = <T,>({ items, activeId, onChange, className, autoFocusKey }: IVerticalTabsProps<T>) => {
  const listRef = useRef<HTMLUListElement>(null);

  const focusableItems = () =>
    Array.from(listRef.current?.querySelectorAll<HTMLLIElement>('[data-vtab-item="true"]') ?? []);

  const moveFocus = (from: EventTarget, delta: number) => {
    const els = focusableItems();
    const idx = els.indexOf(from as HTMLLIElement);
    if (idx === -1 || els.length === 0) {
      return;
    }
    els[(idx + delta + els.length) % els.length].focus({ preventScroll: true });
  };

  useEffect(() => {
    if (autoFocusKey == null) {
      return;
    }
    const els = focusableItems();
    if (els.length === 0) {
      return;
    }
    (els.find((el) => el.dataset.vtabActive === "true") ?? els[0]).focus({ preventScroll: true });
  }, [autoFocusKey, items]);

  return (
    <ul ref={listRef} className={cx(style.tabsListVertical, className)}>
      {items.map((item) => {
        const isActive = Object.is(item.id, activeId);
        const isDisabled = !!item.disabled;

        return (
          <li
            key={item.key}
            role="button"
            tabIndex={isDisabled ? -1 : 0}
            title={item.tooltip}
            data-vtab-item={!isDisabled || undefined}
            data-vtab-active={isActive || undefined}
            className={cx(style.tab, isActive && style.tabActive, isDisabled && style.tabDisabled)}
            onClick={() => {
              if (!isDisabled) {
                onChange(item.id);
              }
            }}
            onKeyDown={(event) => {
              if (isDisabled) {
                return;
              }
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onChange(item.id);
              } else if (event.key === "ArrowDown") {
                event.preventDefault();
                moveFocus(event.currentTarget, 1);
              } else if (event.key === "ArrowUp") {
                event.preventDefault();
                moveFocus(event.currentTarget, -1);
              }
            }}
          >
            <span className={style.tabTitle}>{item.title}</span>
            {item.description && <div className={style.tabDescription}>{item.description}</div>}
          </li>
        );
      })}
    </ul>
  );
};

interface IVerticalTabsLayoutProps {
  sidebar: ReactNode;
  children: ReactNode;
  className?: string;
  sidebarClassName?: string;
  contentClassName?: string;
}

export const VerticalTabsLayout = ({
  sidebar,
  children,
  className,
  sidebarClassName,
  contentClassName,
}: IVerticalTabsLayoutProps) => (
  <div className={cx(style.layout, className)}>
    <aside className={cx(style.sidebar, sidebarClassName)}>{sidebar}</aside>
    <main className={contentClassName}>{children}</main>
  </div>
);

